import {
  ArrowRight,
  CheckCircle2,
  Download,
  ExternalLink,
  FileArchive,
  FileText,
  XCircle,
} from "lucide-react";
import { PageBreadcrumb } from "@/components/PageBreadcrumb";
import { SiteLayout } from "@/components/SiteLayout";
import { Button } from "@/components/ui/button";
import { useSite } from "@/hooks/use-site";
import { localizeUrl } from "@/lib/site";

const BRAND_ASSET_ROOT = "/files/brand";

type LogoVariant = {
  id: "primary" | "on-dark" | "on-blue";
  label: string;
  previewClassName: string;
};

function AssetDownloads({
  basename,
  asset,
  label,
}: {
  basename: string;
  asset: "logo" | "icon";
  label: string;
}) {
  return (
    <div className="flex items-center gap-4 border-t border-border/60 px-4 py-3 text-xs font-medium">
      {(["svg", "png"] as const).map((format) => (
        <a
          key={format}
          href={`${BRAND_ASSET_ROOT}/${basename}.${format}`}
          download
          data-umami-event="brand-asset-download"
          data-umami-event-asset={asset}
          data-umami-event-variant={label}
          data-umami-event-format={format}
          className="inline-flex items-center gap-1.5 text-muted-foreground transition-colors hover:text-primary"
        >
          <Download aria-hidden className="h-3.5 w-3.5" />
          {format.toUpperCase()}
        </a>
      ))}
    </div>
  );
}

export function BrandPage() {
  const { messages, site, href } = useSite();
  const contactHref = localizeUrl("/contacts?inquiry=brand#inquiry-form", site.locale, href);
  const logoVariants: LogoVariant[] = [
    {
      id: "primary",
      label: messages.brandPrimary,
      previewClassName: "bg-white",
    },
    {
      id: "on-dark",
      label: messages.brandOnDark,
      previewClassName: "bg-black",
    },
    {
      id: "on-blue",
      label: messages.brandOnBlue,
      previewClassName: "bg-[#1688F3]",
    },
  ];
  const colors = [
    {
      name: messages.brandColorBlue,
      hex: "#1688F3",
      rgb: "22 / 136 / 243",
      cmyk: "75 / 44 / 0 / 0%",
      swatchClassName: "bg-[#1688F3]",
    },
    {
      name: messages.brandColorWhite,
      hex: "#FFFFFF",
      rgb: "255 / 255 / 255",
      cmyk: "0 / 0 / 0 / 0%",
      swatchClassName: "border border-border bg-white",
    },
    {
      name: messages.brandColorBlack,
      hex: "#000000",
      rgb: "0 / 0 / 0",
      cmyk: "0 / 0 / 0 / 100%",
      swatchClassName: "bg-black",
    },
  ];
  const approvedUses = [
    messages.brandDoOfficialAssets,
    messages.brandDoContrast,
    messages.brandDoProportions,
    messages.brandDoAccurateRelationship,
  ];
  const prohibitedUses = [
    messages.brandDontRecolor,
    messages.brandDontDistort,
    messages.brandDontEffects,
    messages.brandDontRearrange,
  ];
  const permissions = [
    messages.brandPermissionPress,
    messages.brandPermissionPartners,
    messages.brandPermissionAccuracy,
  ];

  return (
    <SiteLayout>
      <PageBreadcrumb items={[{ label: messages.footerLinkBrand }]} />

      <section className="relative isolate overflow-hidden border-b border-border/60">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 -z-20 bg-grid opacity-35"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute left-1/2 top-0 -z-10 h-[30rem] w-[62rem] -translate-x-1/2 rounded-full bg-primary/12 blur-3xl"
        />
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-6 py-16 lg:grid-cols-[minmax(0,0.95fr)_minmax(32rem,1.05fr)] lg:py-24">
          <div className="max-w-2xl animate-fade-up">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">
              {messages.brandEyebrow}
            </p>
            <h1 className="mt-5 text-balance text-4xl font-semibold leading-tight tracking-tight sm:text-5xl">
              {messages.brandHeading}
            </h1>
            <p className="mt-6 max-w-xl text-base leading-7 text-muted-foreground sm:text-lg">
              {messages.brandIntro}
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Button asChild size="lg" className="h-11 rounded-lg px-5">
                <a
                  href={`${BRAND_ASSET_ROOT}/inext-brand-assets.zip`}
                  download
                  data-umami-event="brand-pack-download"
                >
                  <FileArchive aria-hidden className="h-4 w-4" />
                  {messages.brandDownloadPack}
                </a>
              </Button>
              <Button asChild size="lg" variant="outline" className="h-11 rounded-lg px-5">
                <a
                  href={`${BRAND_ASSET_ROOT}/inext-logo-guide.pdf`}
                  download
                  data-umami-event="brand-guide-download"
                >
                  <FileText aria-hidden className="h-4 w-4" />
                  {messages.brandDownloadGuide}
                </a>
              </Button>
            </div>
            <p className="mt-4 text-xs leading-5 text-muted-foreground">
              {messages.brandDownloadNote}
            </p>
          </div>

          <div className="relative animate-fade-up" style={{ animationDelay: "100ms" }}>
            <div className="overflow-hidden rounded-3xl border border-border/60 bg-card shadow-[var(--shadow-elevated)]">
              <div className="flex min-h-72 items-center justify-center bg-[#1688F3] px-12 py-16 sm:min-h-80">
                <img
                  src={`${BRAND_ASSET_ROOT}/inext-logo-on-blue.svg`}
                  alt={messages.brandLogoAlt}
                  className="w-full max-w-md"
                />
              </div>
              <div className="grid grid-cols-3 divide-x divide-border/60 border-t border-border/60">
                <div className="h-14 bg-white" aria-label={messages.brandPrimary} />
                <div className="h-14 bg-black" aria-label={messages.brandOnDark} />
                <div className="h-14 bg-[#1688F3]" aria-label={messages.brandOnBlue} />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="logos" className="border-b border-border/60">
        <div className="mx-auto max-w-7xl px-6 py-20 lg:py-28">
          <div className="max-w-3xl">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">
              {messages.brandLogoEyebrow}
            </p>
            <h2 className="mt-4 text-3xl font-semibold tracking-tight sm:text-5xl">
              {messages.brandLogoTitle}
            </h2>
            <p className="mt-5 text-base leading-7 text-muted-foreground">
              {messages.brandLogoDescription}
            </p>
          </div>

          {[
            { title: messages.brandFullLogo, asset: "logo" as const },
            { title: messages.brandIcon, asset: "icon" as const },
          ].map(({ title, asset }) => (
            <div key={asset} className="mt-12">
              <h3 className="text-sm font-semibold uppercase tracking-widest text-muted-foreground">
                {title}
              </h3>
              <div className="mt-4 grid gap-5 md:grid-cols-3">
                {logoVariants.map((variant) => {
                  const basename = `inext-${asset}-${variant.id}`;
                  return (
                    <article
                      key={`${asset}-${variant.id}`}
                      className="overflow-hidden rounded-2xl border border-border/70 bg-card shadow-[var(--shadow-soft)]"
                    >
                      <div
                        className={`flex h-52 items-center justify-center px-8 ${variant.previewClassName}`}
                      >
                        <img
                          src={`${BRAND_ASSET_ROOT}/${basename}.svg`}
                          alt={`${messages.brandLogoAlt} — ${variant.label}`}
                          className={asset === "logo" ? "w-full max-w-[17rem]" : "h-28 w-28"}
                          loading="lazy"
                        />
                      </div>
                      <div className="px-4 py-3">
                        <p className="text-sm font-semibold text-foreground">{variant.label}</p>
                      </div>
                      <AssetDownloads basename={basename} asset={asset} label={variant.id} />
                    </article>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="border-b border-border/60 bg-muted/25">
        <div className="mx-auto max-w-7xl px-6 py-20 lg:py-28">
          <div className="max-w-3xl">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">
              {messages.brandColorsEyebrow}
            </p>
            <h2 className="mt-4 text-3xl font-semibold tracking-tight sm:text-5xl">
              {messages.brandColorsTitle}
            </h2>
            <p className="mt-5 text-base leading-7 text-muted-foreground">
              {messages.brandColorsDescription}
            </p>
          </div>

          <div className="mt-10 grid gap-5 md:grid-cols-3">
            {colors.map((color) => (
              <article
                key={color.hex}
                className="overflow-hidden rounded-2xl border border-border/70 bg-card shadow-[var(--shadow-soft)]"
              >
                <div className={`h-36 ${color.swatchClassName}`} />
                <div className="p-5">
                  <div className="flex items-baseline justify-between gap-4">
                    <h3 className="text-lg font-semibold">{color.name}</h3>
                    <code className="text-sm font-semibold text-primary">{color.hex}</code>
                  </div>
                  <dl className="mt-5 space-y-2 text-sm">
                    <div className="flex justify-between gap-5">
                      <dt className="text-muted-foreground">RGB</dt>
                      <dd className="font-medium">{color.rgb}</dd>
                    </div>
                    <div className="flex justify-between gap-5">
                      <dt className="text-muted-foreground">CMYK</dt>
                      <dd className="font-medium">{color.cmyk}</dd>
                    </div>
                  </dl>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-border/60">
        <div className="mx-auto max-w-7xl px-6 py-20 lg:py-28">
          <div className="max-w-3xl">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">
              {messages.brandTypographyEyebrow}
            </p>
            <h2 className="mt-4 text-3xl font-semibold tracking-tight sm:text-5xl">
              {messages.brandTypographyTitle}
            </h2>
          </div>

          <div className="mt-10 grid gap-6 lg:grid-cols-2">
            <article className="rounded-2xl border border-border/70 bg-card p-7 shadow-[var(--shadow-soft)] sm:p-9">
              <p className="text-sm font-medium text-muted-foreground">Roboto</p>
              <p
                className="mt-8 break-words text-5xl font-normal tracking-tight sm:text-6xl"
                style={{ fontFamily: '"Roboto", "Helvetica Neue", Arial, sans-serif' }}
              >
                Aa Bb Cc 0123
              </p>
              <p className="mt-8 text-sm leading-6 text-muted-foreground">
                {messages.brandTypographyDescription}
              </p>
              <a
                href="https://fonts.google.com/specimen/Roboto"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
              >
                {messages.brandTypographyLink}
                <ExternalLink aria-hidden className="h-3.5 w-3.5" />
              </a>
            </article>

            <article className="rounded-2xl border border-border/70 bg-[#1688F3] p-7 text-white shadow-[var(--shadow-soft)] sm:p-9">
              <p className="text-sm font-medium text-white/75">{messages.brandNameTitle}</p>
              <p
                className="mt-8 text-5xl font-normal tracking-tight sm:text-6xl"
                style={{ fontFamily: '"Roboto", "Helvetica Neue", Arial, sans-serif' }}
              >
                inext
              </p>
              <p className="mt-8 max-w-lg text-sm leading-6 text-white/80">
                {messages.brandNameDescription}
              </p>
            </article>
          </div>
        </div>
      </section>

      <section className="border-b border-border/60 bg-muted/25">
        <div className="mx-auto max-w-7xl px-6 py-20 lg:py-28">
          <div className="max-w-3xl">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">
              {messages.brandUsageEyebrow}
            </p>
            <h2 className="mt-4 text-3xl font-semibold tracking-tight sm:text-5xl">
              {messages.brandUsageTitle}
            </h2>
            <p className="mt-5 text-base leading-7 text-muted-foreground">
              {messages.brandUsageDescription}
            </p>
          </div>

          <div className="mt-10 grid gap-6 lg:grid-cols-2">
            <article className="rounded-2xl border border-border/70 bg-card p-7 shadow-[var(--shadow-soft)] sm:p-9">
              <h3 className="flex items-center gap-2 text-lg font-semibold">
                <CheckCircle2 aria-hidden className="h-5 w-5 text-emerald-600" />
                {messages.brandDoTitle}
              </h3>
              <ul className="mt-6 space-y-4">
                {approvedUses.map((item) => (
                  <li key={item} className="flex gap-3 text-sm leading-6 text-muted-foreground">
                    <CheckCircle2 aria-hidden className="mt-1 h-4 w-4 shrink-0 text-emerald-600" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </article>

            <article className="rounded-2xl border border-border/70 bg-card p-7 shadow-[var(--shadow-soft)] sm:p-9">
              <h3 className="flex items-center gap-2 text-lg font-semibold">
                <XCircle aria-hidden className="h-5 w-5 text-red-600" />
                {messages.brandDontTitle}
              </h3>
              <ul className="mt-6 space-y-4">
                {prohibitedUses.map((item) => (
                  <li key={item} className="flex gap-3 text-sm leading-6 text-muted-foreground">
                    <XCircle aria-hidden className="mt-1 h-4 w-4 shrink-0 text-red-600" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </article>
          </div>
        </div>
      </section>

      <section className="relative isolate overflow-hidden">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(circle_at_80%_20%,rgba(22,136,243,0.12),transparent_42%)]"
        />
        <div className="mx-auto grid max-w-7xl gap-10 px-6 py-20 lg:grid-cols-[minmax(0,1fr)_minmax(24rem,0.7fr)] lg:items-center lg:py-28">
          <div className="max-w-3xl">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">
              {messages.brandPermissionsEyebrow}
            </p>
            <h2 className="mt-4 text-3xl font-semibold tracking-tight sm:text-5xl">
              {messages.brandPermissionsTitle}
            </h2>
            <p className="mt-5 text-base leading-7 text-muted-foreground">
              {messages.brandPermissionsDescription}
            </p>
          </div>

          <div className="rounded-2xl border border-border/70 bg-card p-7 shadow-[var(--shadow-elevated)] sm:p-8">
            <ul className="space-y-4">
              {permissions.map((item) => (
                <li key={item} className="flex gap-3 text-sm leading-6 text-muted-foreground">
                  <CheckCircle2 aria-hidden className="mt-1 h-4 w-4 shrink-0 text-primary" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <Button asChild variant="outline" className="mt-7 w-full justify-between rounded-lg">
              <a href={contactHref}>
                {messages.brandContactCta}
                <ArrowRight aria-hidden className="h-4 w-4" />
              </a>
            </Button>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
